import React, { Component } from "react";
import { NavLink, withRouter } from "react-router-dom";
import { Navbar, Nav, Badge } from "react-bootstrap";
import { Auth } from "aws-amplify";
import CompanyLocationSelector from '../common/CompanyLocationSelector';
import ThemeToggle from '../common/ThemeToggle';
import { clearSessionKeepingTheme } from '../../util/theme';
import { HTTPService } from "./../../services/httpService";
import ScopeService from "./../../services/scopeService";
import { getMenuForRole } from "../../config/roleMenus";
import { SECTION_ORDER, SECTIONS, MENU_ITEMS } from "../../config/navConfig";
import { isAllDealerships } from "../../util/locationUtils";
import { isTechnician as isTechRole, isManagerOrAbove } from "../../util/permissions";

const API = new HTTPService();

class navTopBar extends Component {
  state = {
    menu: false,
    namePage: "Requestimates",
    selectedRequestimate: {},
    myWorkCount: 0
  };

  setNamePage = (namePage) => {
    this.setState({ namePage });
    this.setState({ menu: false });
  };

  openMenu = async () => {
    this.setState({ menu: true });
  };

  closeMenu = async () => {
    this.setState({ menu: false });
  };

  doSubmit = async () => {
    Auth.signOut()
      .then(() => {
        clearSessionKeepingTheme();
        this.props.history.push("/login");
      })
      .catch((err) => {
        //console.log(err);
      });
  };

  handleCancel = () => {
    this.setState({ selectedRequestimate: {} });
  };

  async componentDidMount() {
    // Pages that render their own top toolbar (e.g. MyWork) suppress this bar to
    // save a row, but still need to open the slide-out menu. They dispatch a
    // window event instead of reaching across components for the hamburger.
    this._openMenuHandler = () => this.openMenu();
    window.addEventListener('jv:open-mobile-menu', this._openMenuHandler);

    // Load technician's assigned work count
    if (this.isTechnician()) {
      await this.loadMyWorkCount();
    }
  }

  componentWillUnmount() {
    if (this._openMenuHandler) {
      window.removeEventListener('jv:open-mobile-menu', this._openMenuHandler);
    }
  }

  // Check if current user is a technician
  isTechnician = () => {
    return this.props.userType === "installer" && isTechRole(ScopeService.getUserRole());
  };

  // Load count of services assigned to technician
  loadMyWorkCount = async () => {
    try {
      const response = await API.get(`/requestService/technician/${this.props.userId}`);
      if (response.data && Array.isArray(response.data)) {
        // Count services that are not finished
        const activeServices = response.data.filter(s => s.service_state !== 'finished');
        this.setState({ myWorkCount: activeServices.length });
      }
    } catch (error) {
      console.error('Error loading My Work count:', error);
    }
  };

  // Render a single menu item from the role-driven menu config. Mobile nav
  // is a flat list (no section headers) — desktop sidebar handles sections.
  renderMenuItem = (item) => {
    // System Help and other external links open in a new tab.
    if (item.externalUrl) {
      const url = (item.urls && item.urls[this.props.userType]) || (item.urls && item.urls.installer);
      if (!url) return null;
      return (
        <li key={item.id}>
          <a
            className="text-light"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => this.setNamePage(item.label)}
          >
            <i className={item.icon} aria-hidden="true"></i> {item.label}
          </a>
        </li>
      );
    }

    // Items with a submenu (e.g. Automations) — mobile just links to the
    // parent route; the expanded submenu lives on desktop.
    const showMyWorkBadge = item.id === 'myWork' && this.state.myWorkCount > 0;

    return (
      <li key={item.id}>
        <NavLink
          activeClassName="active"
          onClick={() => this.setNamePage(item.label)}
          className="text-light"
          to={item.route}
        >
          {showMyWorkBadge && (
            <Badge pill variant="danger" className="mr-1">
              {this.state.myWorkCount}
            </Badge>
          )}
          <i className={item.icon} aria-hidden="true"></i> {item.label}
        </NavLink>
      </li>
    );
  };

  render() {
    var imageName = require("./../../assets/Jobvious_white_logo_base.svg");

    const selectorProps = {
      companyName: this.props.title,
      userId: this.props.userId,
      userType: this.props.userType,
      userRole: this.props.userRole,
      locations: this.props.locations,
      selectedLocationId: this.props.selectedLocationId,
      onSwitch: this.props.onSwitch,
      onLocationChange: this.props.onLocationChange,
      selectedDealerId: this.props.selectedDealerId,
      groupId: this.props.groupId,
    };

    // Mobile nav menu rules:
    //   - Installer owner/admin/manager → full WORK options + MyWork. Mobile
    //     hosts WORK-section views only; other sections aren't mobile-styled,
    //     so REFERENCE/SYSTEM stay on the desktop sidebar.
    //   - Installer technician/viewer → lean, MyWork-centric tech menu.
    //   - Dealer (single or group) → role-driven menu, filtered to WORK section.
    const isInstaller = this.props.userType === 'installer';
    const role = (this.props.userRole || '').toLowerCase();
    const isManagement = isInstaller && isManagerOrAbove(role);
    // Management installers keep their real role (full installer menu);
    // technician/viewer are funneled into the tech menu shape.
    const effectiveRole = (isInstaller && !isManagement) ? 'technician' : this.props.userRole;
    const menu = getMenuForRole(
      this.props.userType,
      effectiveRole,
      this.props.access,
      {
        technicianCanActionLeads: true,
        // Group admin sees the group menu only on "All Dealerships"; drilling
        // into a dealership shows that dealership's full menu. Menu-only toggle
        // — the grant flag is unchanged elsewhere. (Matches navBar.jsx.)
        isDealerGroup: ScopeService.isDealerGroupUser() && isAllDealerships(this.props.selectedDealerId),
      }
    );
    let allItems = SECTION_ORDER.flatMap(section => menu[section] || []);
    const isDealerSide = this.props.userType === 'dealer';
    if (isDealerSide || isManagement) {
      // Mobile only hosts WORK-section views; other sections aren't mobile-styled.
      allItems = allItems.filter(item => item.section === SECTIONS.WORK);
    }
    // Management installers also get MyWork (the tech home screen) on mobile,
    // surfaced first ahead of the full Work options.
    if (isManagement) {
      allItems = [{ ...MENU_ITEMS.myWork }, ...allItems];
    }

    if (this.state.menu) {
      return (
        <React.Fragment>
          <Navbar
            onToggle={this.closeMenu}
            expanded={true}
            bg="dark"
            variant="dark"
            expand="lg"
            className="bg-menu shadow-sm mb-2"
          >
            <Navbar.Toggle aria-controls="basic-navbar-nav" />
            <div className="navbar-brand">
              <img
                src={imageName}
                alt=""
                height="40"
                className="d-inline-block align-top mt--2"
              />
            </div>

            <Navbar.Collapse id="basic-navbar-nav" className="pt-5">
              {/* Unified Company/Location Selector */}
              <div className="mb-3">
                <CompanyLocationSelector
                  {...selectorProps}
                  variant="mobile-expanded"
                  onAfterSelect={() => this.setState({ menu: false })}
                />
              </div>

              <Nav className="mr-auto">
                {allItems.map(item => this.renderMenuItem(item))}
              </Nav>

              <div className="text-center mt-5">
                <button
                  className="btn btn-link text-white"
                  onClick={this.doSubmit}
                >
                  SIGN OUT
                </button>
              </div>
            </Navbar.Collapse>
          </Navbar>
        </React.Fragment>
      );
    } else {
      // MyWork renders its own slim top toolbar (with an inline hamburger that
      // opens this menu via the 'jv:open-mobile-menu' event), so suppress the
      // redundant company bar there to reclaim the row.
      if (this.props.location && this.props.location.pathname === '/mywork') {
        return null;
      }
      return (
        <React.Fragment>
          <div className="pos-f-t text-center">
            <nav className="navbar navbar-light bg-light text-center">
              <button
                className="navbar-toggler"
                type="button"
                onClick={this.openMenu}
              >
                <span className="navbar-toggler-icon"></span>
              </button>
              {/* Unified Company/Location Selector - collapsed bar */}
              <span className="navbar-brand">
                <CompanyLocationSelector
                  {...selectorProps}
                  variant="mobile-collapsed"
                />
              </span>
              <ThemeToggle />
            </nav>
          </div>
        </React.Fragment>
      );
    }
  }
}

export default withRouter(navTopBar);
